import { DatePicker } from '@components/common';
import { useBookings } from '@hooks/accommodation';
import { useAppSelector } from '@hooks/useRedux';
import { guestLabel } from '@lib/utils';
import { selectAccommodationProperty } from '@store/accommodation';
import { AlertTriangle, Download, RefreshCw } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { exportToCsv, fmtAmount, fmtDate } from '../reports/reportUtils';

const PAGE_SIZE = 20;

function todayIso() {
  return new Date().toISOString().split('T')[0];
}

function daysOverdue(checkOut: string, asOf: string): number {
  const diff = new Date(asOf).getTime() - new Date(checkOut).getTime();
  return Math.max(0, Math.ceil(diff / 86400000));
}

export const OverstaysPage: React.FC = () => {
  const property = useAppSelector(selectAccommodationProperty);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [asOfDate, setAsOfDate] = useState(todayIso);
  const [page, setPage] = useState(1);

  const { data: overstays = [], isLoading } = useBookings(property?.id, {
    status: 'checked_in',
    check_out_before: asOfDate,
  });

  const totalPages = Math.max(1, Math.ceil(overstays.length / PAGE_SIZE));
  const paginated = useMemo(
    () => overstays.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [overstays, page],
  );

  const handleExport = () => {
    if (!overstays.length) return;
    exportToCsv(
      `overstays-as-of-${asOfDate}`,
      overstays.map((b) => ({
        Client: guestLabel({
          firstName: b.client?.first_name,
          lastName: b.client?.last_name,
        }),
        Room: b.booking_rooms?.[0]?.room?.room_number ?? '—',
        'Check-In': b.check_in,
        'Check-Out': b.check_out,
        'Days Overdue': daysOverdue(b.check_out, asOfDate),
        Adults: b.adults,
        Kids: b.children,
        'Total Bill': fmtAmount(b.total_amount),
      })),
    );
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['bookings', property?.id] });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Overstays</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            Bookings that have not checked out by their scheduled departure
            date.
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-gray-200 bg-white p-4">
        <DatePicker
          mode="single"
          id="overstay-as-of"
          label="Overstays as of"
          value={asOfDate}
          onChange={(v) => {
            setAsOfDate(v);
            setPage(1);
          }}
        />
        <button
          type="button"
          onClick={handleRefresh}
          disabled={isLoading}
          className="flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        >
          <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
          Refresh
        </button>
        {overstays.length > 0 && (
          <button
            type="button"
            onClick={handleExport}
            className="flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Download size={14} />
            Export CSV
          </button>
        )}
      </div>

      {/* Summary chip */}
      {!isLoading && overstays.length > 0 && (
        <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5">
          <AlertTriangle size={15} className="text-amber-600" />
          <span className="text-sm font-medium text-amber-800">
            {overstays.length} booking
            {overstays.length !== 1 ? 's' : ''} overdue as of{' '}
            {fmtDate(asOfDate)}
          </span>
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl border border-gray-200 bg-white">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-sm text-gray-400">
            Loading…
          </div>
        ) : overstays.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <AlertTriangle size={32} className="text-gray-300" />
            <p className="text-sm font-medium text-gray-500">
              No overstay bookings as of {fmtDate(asOfDate)}.
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <th className="px-4 py-3">Client</th>
                    <th className="px-4 py-3">Room</th>
                    <th className="px-4 py-3">Check-In</th>
                    <th className="px-4 py-3">Check-Out</th>
                    <th className="px-4 py-3 text-center">Days Overdue</th>
                    <th className="px-4 py-3 text-center">Adults</th>
                    <th className="px-4 py-3 text-center">Kids</th>
                    <th className="px-4 py-3 text-right">Total Bill</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginated.map((b) => {
                    const overdue = daysOverdue(b.check_out, asOfDate);
                    return (
                      <tr
                        key={b.id}
                        className="cursor-pointer hover:bg-gray-50/60"
                        onClick={() =>
                          navigate(
                            `/accommodation/bookings/${b.id}?back=/accommodation/overstays`,
                          )
                        }
                      >
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {guestLabel({
                            firstName: b.client?.first_name,
                            lastName: b.client?.last_name,
                          })}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {b.booking_rooms?.[0]?.room?.room_number ?? '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {b.check_in}
                        </td>
                        <td className="px-4 py-3 text-red-600">
                          {b.check_out}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                            {overdue}d
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center text-gray-600">
                          {b.adults}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-600">
                          {b.children}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900">
                          {fmtAmount(b.total_amount)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm text-gray-500">
                <span>
                  Showing{' '}
                  {Math.min((page - 1) * PAGE_SIZE + 1, overstays.length)}–
                  {Math.min(page * PAGE_SIZE, overstays.length)} of{' '}
                  {overstays.length}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                    className="rounded px-2 py-1 hover:bg-gray-100 disabled:opacity-40"
                  >
                    ‹
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (n) => (
                      <button
                        type="button"
                        key={n}
                        onClick={() => setPage(n)}
                        className={`rounded px-2 py-1 ${
                          n === page
                            ? 'bg-pos-primary text-white'
                            : 'hover:bg-gray-100'
                        }`}
                      >
                        {n}
                      </button>
                    ),
                  )}
                  <button
                    type="button"
                    disabled={page === totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="rounded px-2 py-1 hover:bg-gray-100 disabled:opacity-40"
                  >
                    ›
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
